import React from 'react'
import { moneyFromMinor, percentFromScaled } from '../../../utils/UnitConversion.utils'
import { CountryProviderFeeDTO } from './Deposit'
import weChatPaySvg from '../../../../assets/svg/deposit/WE_CHAT_PAY.svg'
import creditCardSvg from '../../../../assets/svg/deposit/CREDIT_CARD.svg'
import mobilePaySvg from '../../../../assets/svg/deposit/MOBILE_PAY.svg'
import epsSvg from '../../../../assets/svg/deposit/EPS.svg'
import applePaySvg from '../../../../assets/svg/deposit/APPLE_PAY.svg'
import revolutPaySvg from '../../../../assets/svg/deposit/REVOLUT_PAY.svg'
import blikSvg from '../../../../assets/svg/deposit/BLIK.svg'
import googlePaySvg from '../../../../assets/svg/deposit/GOOGLE_PAY.svg'
import payByBankSvg from '../../../../assets/svg/deposit/PAY_BY_BANK.svg'
import przelewy24Svg from '../../../../assets/svg/deposit/PRZELEWY_24.svg'
import alipaySvg from '../../../../assets/svg/deposit/ALIPAY.svg'
import idealSvg from '../../../../assets/svg/deposit/IDEAL.svg'
import billieSvg from '../../../../assets/svg/deposit/BILLIE.svg'
import twintSvg from '../../../../assets/svg/deposit/TWINT.svg'
import bancontactSvg from '../../../../assets/svg/deposit/BANCONTACT.svg'

type ProviderStepProps = {
    provider: string | null
    setProvider: (value: string | null) => void
    depositFees: CountryProviderFeeDTO[]
    currencySymbol?: string
    convertFromUSD?: (v: number) => number
}

const PROVIDER_CONFIG: Record<string, { name: string; icon: string }> = {
    CREDIT_CARD: { name: 'Credit Card', icon: creditCardSvg },
    APPLE_PAY: { name: 'Apple Pay', icon: applePaySvg },
    GOOGLE_PAY: { name: 'Google Pay', icon: googlePaySvg },
    PAY_BY_BANK: { name: 'Pay by Bank', icon: payByBankSvg },
    REVOLUT_PAY: { name: 'Revolut Pay', icon: revolutPaySvg },
    MOBILE_PAY: { name: 'MobilePay', icon: mobilePaySvg },
    ALIPAY: { name: 'Alipay', icon: alipaySvg },
    WE_CHAT_PAY: { name: 'WeChat Pay', icon: weChatPaySvg },
    IDEAL: { name: 'iDEAL', icon: idealSvg },
    BANCONTACT: { name: 'Bancontact', icon: bancontactSvg },
    EPS: { name: 'eps', icon: epsSvg },
    PRZELEWY_24: { name: 'Przelewy24', icon: przelewy24Svg },
    TWINT: { name: 'TWINT', icon: twintSvg },
    BILLIE: { name: 'Billie', icon: billieSvg },
    BLIK: { name: 'BLIK', icon: blikSvg },
}

const ProviderStep: React.FC<ProviderStepProps> = ({
                                                       provider,
                                                       setProvider,
                                                       depositFees = [],
                                                       currencySymbol = '$',
                                                       convertFromUSD = (v: number) => v,
                                                   }) => {
    const fees = depositFees || []

    return (
        <div className="grid grid-cols-3 gap-2.5">
            {fees.map((fee, index) => {
                const providerKey = fee.providerFeeType || (fee as unknown as { name?: string }).name || ''
                const isSelected = provider === providerKey

                const feePercent = percentFromScaled(fee.feeBps)
                const fxFeePercent = percentFromScaled(fee.fxFeeBps)
                const fixedFeeUsd = moneyFromMinor(fee.fixedMinor)
                const fixedFeeConverted = convertFromUSD(fixedFeeUsd)

                const config = PROVIDER_CONFIG[providerKey]
                const displayName = config?.name || providerKey.replace(/_/g, ' ') || 'Unknown'

                // Formats base processing fee string dynamically using user currency
                const feeParts: string[] = []
                if (fixedFeeConverted > 0) {
                    feeParts.push(`${currencySymbol}${fixedFeeConverted.toFixed(2)}`)
                }
                if (feePercent > 0) {
                    feeParts.push(`${feePercent}%`)
                }
                const feeString = feeParts.length > 0 ? feeParts.join(' + ') : 'Free'

                // Determine currency badge label: 'USD -> EUR' if converting, otherwise just 'EUR'
                const currencyBadgeLabel = fee.inputCurrency && fee.outputCurrency
                    ? fee.inputCurrency === fee.outputCurrency
                        ? fee.outputCurrency
                        : `${fee.inputCurrency} → ${fee.outputCurrency}`
                    : fee.outputCurrency || fee.inputCurrency || null

                return (
                    <button
                        key={providerKey || index}
                        type="button"
                        onClick={() => setProvider(isSelected ? null : providerKey)}
                        className={`rounded-xl p-3 border transition-all text-left flex flex-col justify-between gap-2 cursor-pointer min-h-[72px] ${
                            isSelected
                                ? 'border-blue-500 bg-blue-500/10 ring-1 ring-blue-500 shadow-xs'
                                : 'border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/60 hover:border-blue-400 dark:hover:border-neutral-700'
                        }`}
                    >
                        <div className="flex items-center justify-between gap-1 w-full">
                            <div className="flex items-center gap-2 min-w-0">
                                {config?.icon ? (
                                    <div className="w-6 h-6 rounded-md bg-neutral-100 dark:bg-neutral-800 p-1 flex items-center justify-center flex-shrink-0 border border-neutral-200/50 dark:border-neutral-700/50">
                                        <img src={config.icon} alt={displayName} className="w-full h-full object-contain" />
                                    </div>
                                ) : (
                                    <div className="w-6 h-6 rounded-md bg-blue-500/10 flex items-center justify-center flex-shrink-0 text-blue-500 font-bold text-[10px]">
                                        {displayName.slice(0, 2).toUpperCase()}
                                    </div>
                                )}
                                <span className="font-semibold text-xs text-neutral-900 dark:text-white truncate">
                                    {displayName}
                                </span>
                            </div>

                            <div className="flex items-center gap-1 flex-shrink-0">
                                {currencyBadgeLabel && (
                                    <span className="text-[9px] font-bold bg-blue-500/15 text-blue-600 dark:text-blue-400 px-1.5 py-0.5 rounded border border-blue-500/20 uppercase">
                                        {currencyBadgeLabel}
                                    </span>
                                )}
                                {fee.kycRequired && (
                                    <span className="text-[8px] font-bold bg-amber-500/15 text-amber-600 dark:text-amber-400 px-1 py-0.5 rounded">
                                        KYC
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="flex flex-col text-[11px] pt-1.5 border-t border-neutral-100 dark:border-neutral-800/60 w-full text-neutral-400 gap-0.5">
                            <div className="flex items-center justify-between">
                                <span>Fee</span>
                                <span className="font-semibold text-neutral-700 dark:text-neutral-300">{feeString}</span>
                            </div>
                            {fxFeePercent > 0 && (
                                <div className="flex items-center justify-between text-[10px]">
                                    <span className="text-amber-500/80">FX Fee</span>
                                    <span className="font-semibold text-amber-500">{fxFeePercent}%</span>
                                </div>
                            )}
                        </div>
                    </button>
                )
            })}
        </div>
    )
}

export default ProviderStep